import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Loader2, ExternalLink, X, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useThemeStore } from '../../store/themeStore';
import { mediaAPI, getErrorMessage } from '../../services/api';
import type { LinkPreview, Platform } from './types';

interface Props {
  linkPreview: LinkPreview | null;
  setLinkPreview: React.Dispatch<React.SetStateAction<LinkPreview | null>>;
  selectedPlatforms: string[];
  platforms: Platform[];
}

export const LinkAttachmentTab: React.FC<Props> = ({
  linkPreview,
  setLinkPreview,
  selectedPlatforms,
  platforms,
}) => {
  const { theme } = useThemeStore();
  const { t } = useTranslation();
  const [linkInput, setLinkInput] = useState('');
  const [isLinkLoading, setIsLinkLoading] = useState(false);

  // Check for platforms that don't support link previews
  const unsupportedPlatforms = selectedPlatforms
    .map((id) => platforms.find((p) => p.id === id))
    .filter((p) => p && !p.supportsLinkPreview);

  const handleLinkPreview = async () => {
    const trimmed = linkInput.trim();
    if (!trimmed) return;
    setIsLinkLoading(true);
    try {
      const resp = await mediaAPI.ogPreview(trimmed);
      setLinkPreview(resp.data);
      toast.success(t('compose_link_preview_loaded', 'Link preview loaded'));
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, t('compose_failed_fetch_preview', 'Failed to fetch link preview')));
    } finally {
      setIsLinkLoading(false);
    }
  };

  const removeLinkPreview = () => {
    setLinkPreview(null);
    setLinkInput('');
  };

  return (
    <>
      <div className="space-y-3">
        <div className="flex gap-2">
          <input
            type="url"
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLinkPreview(); }}
            placeholder={t('compose_paste_url_link', 'Paste any URL to attach a link preview card')}
            className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:border-transparent"
            style={{ outlineColor: theme.hex.primaryRing }}
          />
          <button
            onClick={handleLinkPreview}
            disabled={!linkInput.trim() || isLinkLoading}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all disabled:bg-gray-300"
            style={{ backgroundColor: !linkInput.trim() || isLinkLoading ? undefined : theme.hex.primary }}
          >
            {isLinkLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
            {t('compose_preview_button', 'Preview')}
          </button>
        </div>
        <p className="text-xs text-gray-400">
          {t('compose_link_paste_help', 'Paste a YouTube video, article, or webpage URL. We\u2019ll fetch the preview and attach it to your post.')}
        </p>
      </div>

      {/* Platform compatibility warning */}
      {unsupportedPlatforms.length > 0 && linkPreview && (
        <div className="mt-3 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-700">
            {t('compose_link_platforms_warning', 'Link previews won\u2019t display on {{platforms}}. The URL will be included as plain text instead.', { platforms: unsupportedPlatforms.map((p) => p!.name).join(', ') })}
          </p>
        </div>
      )}

      {/* Link Preview Card */}
      {linkPreview && (
        <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
          {linkPreview.image && (
            <img src={linkPreview.image} alt="" className="w-full h-40 object-cover" />
          )}
          <div className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                {linkPreview.site_name && (
                  <div className="flex items-center gap-1.5 mb-1">
                    {linkPreview.favicon && (
                      <img src={linkPreview.favicon} alt="" className="h-3.5 w-3.5 rounded" />
                    )}
                    <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">
                      {linkPreview.site_name}
                    </span>
                    {linkPreview.link_type && linkPreview.link_type !== 'website' && (
                      <span className="text-[10px] bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">
                        {linkPreview.link_type}
                      </span>
                    )}
                  </div>
                )}
                <h4 className="text-sm font-semibold text-gray-900 line-clamp-2">
                  {linkPreview.title || linkPreview.url}
                </h4>
                {linkPreview.description && (
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{linkPreview.description}</p>
                )}
                <p className="text-[10px] text-gray-400 mt-1.5 truncate flex items-center gap-1">
                  <ExternalLink className="h-2.5 w-2.5" />
                  {linkPreview.domain || linkPreview.url}
                </p>
              </div>
              <button
                onClick={removeLinkPreview}
                className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
